// Shared settings definitions; values and helpers are maintained here.
import { weekdayLabel } from '../auditHistory';


// Feature registry categories (backend/amalgamesh/features.py's own
// `category=` field on each FeatureDef) shown in the Health page's
// feature table — a small, fixed set of lowercase code-style words
// ("core", "auth", "observer") that read fine as a Python kwarg value
// but not as a table cell; this is the one place that table renders
// them, so the fix lives here rather than changing the backend's own
// internal category id.
export const FEATURE_CATEGORY_LABEL: Record<string, string> = {
  core: "Core",
  auth: "Authentication",
  observer: "Observer",
  hazards: "Hazards",
  automations: "Automations",
  platform: "Platform",
  transport: "Transport",
};


export function featureCategoryLabel(category: string): string {
  return FEATURE_CATEGORY_LABEL[category] ?? category;
}


// Settings > System > Feature flags (2026-09-27 first-installer review,
// part5d): the tab was one flat grid of 30 toggles with no structure at
// all. Grouped into the same categories the Health page's own feature
// table already sorts by (featureCategoryLabel above) — this is the one
// place besides HealthPage that needs a feature's category, and the
// config schema itself carries no category (FieldSpec has no such field),
// so this is a small, explicitly-mirrored copy of each feature's own
// `category=` in backend/amalgamesh/features.py's REGISTRY — id -> category
// only, checked against that file directly, not guessed. A feature id
// missing here (e.g. a future addition) just renders ungrouped, at the
// end, rather than disappearing.
export const FEATURE_ID_CATEGORY: Record<string, string> = {
  core_ingest: "core",
  meshtastic_vnode_transport: "transport",
  meshcore_vnode_transport: "transport",
  packet_view: "core",
  node_list: "core",
  radio_config_api: "core",
  live_stream: "core",
  health_monitoring: "core",
  config_api: "core",
  regions: "core",
  observers_api: "core",
  history_api: "core",
  retention_jobs: "core",
  external_observers: "observer",
  corescope_observer: "observer",
  meshview_observer: "observer",
  identity_reconciliation: "platform",
  auth_local: "core",
  mqtt_ingest: "observer",
  auth_oidc: "auth",
  audit_detectors: "core",
  ask_chat: "core",
  simulated_nodes: "core",
  message_send: "core",
  coverage_views: "core",
  hazard_adapters: "hazards",
  fire_hazard_adapter: "hazards",
  weather_hazard_adapter: "hazards",
  radio_control_api: "core",
  automations: "automations",
};


// Fixed, ordered category grouping for the Feature flags tab (Settings >
// System). Not alphabetical: baseline app first, then radio transports,
// then external data sources, then auth, then platform-level (identity
// reconciliation), then hazards, then automations that build on both
// message_send and hazards. A category with no member in the live schema
// is simply never rendered — see groupFeatureFields below.
export const FEATURE_CATEGORY_ORDER: readonly string[] = ["core", "transport", "observer", "auth", "platform", "hazards", "automations"];


export function featureCategoryForId(featureId: string): string | undefined {
  return FEATURE_ID_CATEGORY[featureId];
}


// --- Settings/config schema labels (Settings page, 2026-09-21) -------------
// GET /api/config/schema is a live, growing registry (129 flat fields, a
// "transports" dynamic-keyed section, and 6 list sections as of this
// writing) — the same "backend is the source of truth, UI walks whatever it
// declares" principle as NAV_DESTINATIONS/AUDIT_SUBMENUS above. A brand-new
// section or field a future backend change adds must never render blank —
// so this is a REGISTRY OF OVERRIDES on top of a mechanical fallback
// (humanizeConfigPath), not a hand-maintained exhaustive list: only entries
// where the mechanical title-casing is genuinely wrong (an acronym rendered
// as a capitalized word — "Oidc", "Ttl", "Tls", "Id" — a unit suffix left
// as a bare trailing letter — "Check Interval S" — or a brand's own fixed
// internal capitalization — "MeshCore", "CoreScope") are listed here.
//
// Keys match the schema's OWN `path` string exactly: a flat field's full
// dotted path ("watchdog.stale_after_s"), and — verified directly against
// the live schema response, not assumed — a BARE, unqualified key for every
// dynamic_keyed_sections/list_sections item field ("trust_tier", "id",
// "timeout_s"...), because that is genuinely what the schema puts in that
// field's own `path` property; there is no "section.field"-qualified form
// for these anywhere in the response. That means the same bare key can
// legitimately appear in more than one list section's item_fields (e.g.
// "id" in observers.sources/regions/mqtt.brokers/hazards.sources, "host"/
// "port" in both transports and mqtt.brokers) — checked this is never a
// case of one key meaning two different THINGS (a host is a host, a
// timeout in seconds is a timeout in seconds, regardless of which list it's
// configuring), so one shared label per bare key is correct everywhere it's
// used, not a collision to resolve. The only collision actually checked for
// and ruled out: none of the 10 transports' bare keys match any flat
// field's full (dotted, section-prefixed) path.
export const CONFIG_SECTION_LABEL: Record<string, string> = {
  // Flat sections (17)
  server: "Server",
  database: "Database",
  logging: "Logging",
  watchdog: "Watchdog",
  retention: "Retention",
  reconciliation: "Reconciliation",
  audit: "Analysis",
  route_inference: "Route Inference",
  ask: "Ask",
  auth: "Authentication",
  observers: "Observers",
  mqtt: "MQTT Settings",
  messaging: "Messaging",
  hazards: "Hazards",
  api: "API",
  meshcore: "MeshCore",
  mesh_regions: "Mesh Regions",
  meshmapper: "MeshMapper",
  freqmapper: "FreqMapper",
  // Added with the Settings-coverage pass (part 4, 2026-09-27): features.*
  // (feature flags, generated per-feature from features.py's registry —
  // see FEATURES_FIELDS's own comment in config_schema.py), map.*/
  // display.* (frontend-only map centre/zoom + unit prefs), and the three
  // Advanced measured-constant groups (position_estimate, airtime,
  // mesh_health) — all render fine mechanically via humanizeConfigPath
  // already, but listed explicitly for the same reason every other
  // section here is: this registry documents the full live schema, not
  // just the cases the fallback gets wrong.
  features: "Feature Flags",
  map: "Map",
  display: "Display",
  nodes: "Nodes",
  position_estimate: "Position Estimate",
  airtime: "Airtime",
  mesh_health: "Mesh Health",
  // Dynamic-keyed section (1)
  transports: "Radios",
  // List sections (6)
  "observers.sources": "Observer Sources",
  regions: "Alert Areas",
  "mqtt.brokers": "MQTT Brokers",
  "mqtt.channels": "MQTT Channels",
  "hazards.sources": "Hazard Sources",
  "hazards.region_routes": "Hazard Region Routes",
};


export const CONFIG_FIELD_LABEL: Record<string, string> = {
  // --- flat fields: full dotted path -------------------------------------
  "watchdog.check_interval_s": "Check interval (seconds)",
  "watchdog.stale_after_s": "Stale after (seconds)",
  "retention.prune_interval_s": "Prune interval (seconds)",
  "retention.rollup_interval_s": "Rollup interval (seconds)",
  "retention.corescope_packet_retention_days": "CoreScope packet retention (days)",
  "retention.corescope_prune_batch_size": "CoreScope prune batch size",
  "reconciliation.meshcore_prefix_length_hex": "MeshCore prefix length (hex characters)",
  "reconciliation.position_proximity_km": "Position proximity (km)",
  "reconciliation.scan_interval_s": "Scan interval (seconds)",
  "audit.cache_ttl_s": "Cache TTL (seconds)",
  "audit.recalculate_cooldown_s": "Recalculation cooldown (inactive)",
  "audit.snapshot_memory_limit_mb": "Snapshot pass memory limit (MB)",
  "audit.snapshot_memory_cache_mb": "Snapshot quick-access cache size (MB)",
  "audit.parallel_groups": "Report groups calculated at once",
  "audit.paths_arrival": "Live Paths updates",
  "audit.rf_health_arrival": "Live RF Health updates",
  "audit.backbone_arrival": "Live Backbone updates",
  "audit.audit_table_arrival": "Live Audit table updates",
  "audit.scope_stale_after_days": "Days before a repeater's scope is marked stale",
  "audit.scope_drop_after_days": "Days before a repeater's scope is dropped",
  "audit.history_snapshot_weekday": "History snapshot day",
  "audit.history_snapshot_time": "History snapshot time (HH:MM)",
  "audit.history_snapshot_timezone": "History snapshot time zone",
  "audit.history_retention_weeks": "History retention (weeks)",
  // Detector settings are grouped and labelled by each check's own topic
  // name (config_schema.py's own AUDIT_FIELDS grouping/comments), never by
  // its raw "D<n>" internal code (2026-09-27 first-installer review,
  // part5d) — e.g. "Advert interval: green from (hours)", not "D1 green
  // from (hours)". audit.d1_fast_threshold_s / d1_mid_threshold_s /
  // d1_slow_threshold_s / d5_collision_severity_radius_km are gone from
  // the schema (2026-09-25 rebuild) and have no entry here any more.
  "audit.d1_bin_width_s": "Advert interval: bin width (seconds)",
  "audit.d2_cluster_window_s": "Boot loop: cluster window (seconds)",
  "audit.null_island_epsilon_deg": "No-position (0,0) tolerance (degrees)",
  "audit.location_terrain_percentile": "Location: Terrain Limit Percentile",
  "audit.location_terrain_limit_margin": "Location: Terrain Limit Margin",
  "audit.location_earth_radius_factor": "Location: Earth Radius Factor",
  "audit.location_estimate_offset_limit_km": "Location: Estimate Offset Limit (km)",
  "audit.location_offset_min_neighbours": "Location: Min Neighbours For Offset",
  "audit.location_min_links": "Location: Min Links To Judge",
  "audit.location_blame_share_pct": "Location: Red At Blame Share (%)",
  "audit.location_yellow_share_pct": "Location: Yellow At Share (%)",
  "audit.backbone_stable_min_days": "Backbone: stable link minimum days",
  "audit.backbone_stable_min_transmissions": "Backbone: stable link minimum transmissions",
  "audit.footprint_min_off_share_pct": "Footprint Minimum Off Share (%)",
  "audit.backbone_umbrella_scope_tokens": "Backbone: regional scopes (broad; ignored when comparing repeaters)",
  "audit.scope_names": "Scope Names",
  "audit.scope_colours": "Scope map colours",
  "audit.top_list_count": "Top talkers and advertisers: rows shown",
  "audit.backbone_regional_degree_ratio_min": "Backbone: regional backbone minimum degree ratio",
  "audit.backbone_tukey_multiplier": "Backbone: regional backbone Tukey fence multiplier",
  "audit.backbone_cutpoint_strand_min": "Backbone: minimum repeaters a cut point must strand",
  "audit.backbone_unit_min_repeaters": "Backbone: minimum repeaters to judge a region or scope",
  "audit.backbone_stale_after_hours": "Backbone: data stale after (hours)",
  "audit.pkts_per_hr_chatty_from": "Chatty node: packets per hour threshold",
  "audit.backbone_repeatability_green_min": "Backbone: repeatability green threshold",
  "audit.backbone_repeatability_yellow_min": "Backbone: repeatability yellow threshold",
  "audit.backbone_stability_green_min": "Backbone: stability green threshold",
  "audit.backbone_stability_yellow_min": "Backbone: stability yellow threshold",
  "audit.backbone_credibility_green_min": "Backbone: credibility green threshold",
  "audit.backbone_credibility_yellow_min": "Backbone: credibility yellow threshold",
  "audit.backbone_reachability_green_min": "Backbone: reachability green threshold",
  "audit.backbone_reachability_yellow_min": "Backbone: reachability yellow threshold",
  "audit.backbone_link_symmetry_green_min": "Backbone: link symmetry green threshold",
  "audit.backbone_link_symmetry_yellow_min": "Backbone: link symmetry yellow threshold",
  "audit.backbone_betweenness_green_min": "Backbone: betweenness green threshold",
  "audit.backbone_betweenness_yellow_min": "Backbone: betweenness yellow threshold",
  "audit.backbone_instability_gap_multiplier": "Backbone: instability gap multiplier",
  "audit.backbone_instability_half_life_hours": "Backbone: instability decay half-life (hours)",
  "audit.backbone_position_estimate_min_neighbours": "Backbone: minimum neighbours for a position estimate",
  "audit.clock_yellow_from_s": "Clock offset yellow from (seconds)",
  "audit.clock_red_from_s": "Clock offset red from (seconds)",
  "audit.d1_policy_floor_s": "Advert interval: policy floor (seconds)",
  "audit.advanced.forward_jump_tolerance_s": "Forward jump tolerance (seconds)",
  "audit.advanced.drift_noise_floor_s": "Drift noise floor (seconds)",
  "audit.advanced.advert_floor_bin_width_s": "Advert floor bin width (seconds)",
  "audit.advanced.measured_recall_p_corescope_only": "Measured recall, CoreScope-only (fraction, 0–1)",
  "ask.secret_ref": "Secret reference",
  "ask.request_timeout_s": "Request timeout (seconds)",
  "ask.tiers.flash_lite.enabled": "Flash-lite tier enabled",
  "ask.tiers.flash_lite.model": "Flash-lite tier model",
  "auth.totp.issuer": "TOTP issuer name",
  "auth.local.enabled": "Local accounts enabled",
  "auth.oidc.enabled": "OIDC enabled",
  "auth.oidc.issuer": "OIDC issuer URL",
  "auth.oidc.discovery_url": "OIDC discovery URL",
  "auth.oidc.client_id": "OIDC client ID",
  "auth.oidc.client_secret_ref": "OIDC client secret reference",
  "auth.oidc.redirect_uri": "OIDC redirect URI",
  "observers.poll_defaults.timeout_s": "Default poll timeout (seconds)",
  "observers.poll_defaults.backoff_initial_s": "Default initial backoff (seconds)",
  "observers.poll_defaults.backoff_max_s": "Default maximum backoff (seconds)",
  "mqtt.use_default_psk_fallback": "Use default PSK fallback",
  "mqtt.defaults.keepalive_s": "Default keepalive interval (seconds)",
  "mqtt.defaults.reconnect_initial_s": "Default initial reconnect backoff (seconds)",
  "mqtt.defaults.reconnect_max_s": "Default maximum reconnect backoff (seconds)",
  "mqtt.defaults.collision_window_s": "Default collision window (seconds)",
  "messaging.chunking.meshcore_max_payload_bytes": "MeshCore max payload (bytes)",
  "hazards.conduit.base_url": "Conduit base URL",
  "hazards.conduit.timeout_s": "Conduit timeout (seconds)",
  "hazards.poll_defaults.timeout_s": "Default poll timeout (seconds)",
  "hazards.poll_defaults.backoff_initial_s": "Default initial backoff (seconds)",
  "hazards.poll_defaults.backoff_max_s": "Default maximum backoff (seconds)",
  "api.pagination.history_packet_buckets_default_s": "History packet buckets default width (seconds)",
  "api.pagination.history_packet_buckets_max_s": "History packet buckets max width (seconds)",
  "api.pagination.history_stats_bucket_default_s": "History stats bucket default width (seconds)",
  "api.pagination.history_stats_bucket_max_s": "History stats bucket max width (seconds)",
  "api.pagination.mqtt_messages_default_limit": "MQTT messages default limit",
  "api.pagination.mqtt_messages_max_limit": "MQTT messages max limit",
  "api.pagination.mqtt_undecryptable_default_limit": "MQTT undecryptable default limit",
  "api.pagination.mqtt_undecryptable_max_limit": "MQTT undecryptable max limit",
  "api.pagination.corescope_packets_default_limit": "CoreScope packets default limit",
  "api.pagination.corescope_packets_max_limit": "CoreScope packets max limit",
  "airtime.hour_boundary_pad_s": "Hour boundary pad (seconds)",
  "airtime.rollup_interval_s": "Rollup interval (seconds)",
  "mesh_health.rollup_interval_s": "Rollup interval (seconds)",
  "mesh_regions.outlines_refresh_hours": "Refresh every (hours)",
  "mesh_regions.top_level_region_scope": "Top-level region scope",
  "observers.corescope_backfill_page_delay_s": "CoreScope backfill page delay (seconds)",
  "observers.corescope_observation_refetch_window_hours": "CoreScope observation refetch window (hours)",
  "observers.corescope_old_observations_delay_s": "CoreScope old observations delay (seconds)",
  "position_estimate.estimate_radius_km": "Estimate radius (km)",
  "position_estimate.max_plausible_link_km": "Max Link Distance (km)",
  "retention.meshcore_link_sync_batch_size": "MeshCore link sync batch size",
  "features.auth_oidc": "OIDC Single Sign-On",
  "features.config_api": "Config API",
  "features.corescope_observer": "CoreScope Observer",
  "features.history_api": "History API",
  "features.meshcore_vnode_transport": "MeshCore Radio Transport",
  "features.mqtt_ingest": "MQTT Ingest",
  "features.observers_api": "Observers API",
  "features.radio_config_api": "Radio Self-Config",
  "features.radio_control_api": "Radio Control",
  // Remaining feature flags (Settings copy-lint pass, 2026-09-27): every
  // entry below copies its feature's own canonical `label=` verbatim from
  // backend/amalgamesh/features.py (the SAME rule the 9 entries above
  // already follow — e.g. "features.auth_oidc" here matches
  // features.py's auth_oidc label exactly) rather than mechanically
  // title-casing the config id, because several ids and their real
  // display names differ outright (core_ingest's real name is "Packet
  // Ingest", not "Core Ingest"; coverage_views's is "Coverage Gaps &
  // Observer Disagreement", not "Coverage Views") — mechanical fallback
  // would show operators a name that doesn't match the feature list.
  "features.meshtastic_vnode_transport": "Meshtastic Radio Transport",
  "features.packet_view": "Live Packet View",
  "features.node_list": "Node List",
  "features.live_stream": "Live WebSocket Stream",
  "features.health_monitoring": "Health & Self-Monitoring",
  "features.regions": "Coverage Regions",
  "features.retention_jobs": "Retention & Rollup Jobs",
  "features.external_observers": "External Observers",
  "features.meshview_observer": "Meshview Observer",
  "features.identity_reconciliation": "Identity Reconciliation",
  "features.auth_local": "Local Accounts",
  "features.audit_detectors": "Analysis Detectors",
  "features.ask_chat": "Ask Chat",
  "features.simulated_nodes": "Simulated Nodes",
  "features.message_send": "Message Send",
  "features.coverage_views": "Coverage Gaps & Observer Disagreement",
  "features.hazard_adapters": "Hazard Adapters",
  // WFIGS/NWS spelled out (2026-09-27 first-installer review, part5d) —
  // full names sourced from backend/amalgamesh/hazards/adapters/fire.py's
  // own module docstring ("WFIGS (Wildland Fire Interagency Geospatial
  // Services)") and the National Weather Service's own CAP alert program
  // name, not invented.
  "features.fire_hazard_adapter": "Fire Hazard Adapter (WFIGS — Wildland Fire Interagency Geospatial Services)",
  "features.weather_hazard_adapter": "Weather Hazard Adapter (NWS — National Weather Service)",
  "features.automations": "Automations (Auto-Reply, Welcome, Scheduled Announcements)",
  "features.core_ingest": "Packet Ingest",

  // Remaining flat fields (Settings copy-lint pass, 2026-09-27): every
  // field left with no explicit entry now gets one, even where the
  // mechanical fallback would already read fine, so no Settings field
  // ever depends on humanizeConfigPath at all.
  "database.read_pool_size": "Read-only connection pool size (0 = automatic)",
  "logging.level": "Log level",
  "retention.node_sightings_raw_days": "Node sightings retention (days)",
  "retention.packets_raw_days": "Packet retention (days)",
  "retention.chat_messages_days": "Chat message retention (days)",
  "retention.packets_analysis_window_days": "Packet analysis window (days)",
  "retention.telemetry_raw_days": "Telemetry retention (days)",
  "retention.telemetry_rollup_retention_days": "Telemetry rollup retention (days)",
  "retention.admin_audit_log_retention_days": "Admin audit log retention (days)",
  "retention.meshview_reception_raw_retention_days": "Meshview reception retention (days)",
  "retention.meshview_packet_retention_days": "Meshview packet retention (days)",
  "retention.meshview_rollup_batch_size": "Meshview rollup batch size",
  "retention.meshview_prune_batch_size": "Meshview prune batch size",
  "retention.meshview_direct_link_sync_batch_size": "Meshview direct-link sync batch size",
  "retention.d3_rollup_retention_days": "Loop-detect evidence retention (days)",
  "retention.link_events_retention_days": "Repeater link event retention (days)",
  "reconciliation.enabled": "Enabled",
  "reconciliation.auto_merge_enabled": "Auto-merge enabled",
  "reconciliation.auto_merge_min_score": "Auto-merge minimum score",
  "reconciliation.candidate_min_score": "Candidate minimum score",
  "reconciliation.weight_prefix_match": "Prefix-match weight",
  "reconciliation.weight_name_match": "Name-match weight",
  "reconciliation.weight_position_proximity": "Position-proximity weight",
  "reconciliation.weight_temporal_overlap": "Temporal-overlap weight",
  "reconciliation.temporal_adjacency_hours": "Temporal adjacency (hours)",
  "audit.d1_sample_size_confidence_z": "Advert interval: sample-size confidence (z-value)",
  "audit.d1_green_from_hours": "Advert interval: mesh policy threshold (hours)",
  "audit.d1_yellow_from_hours": "Advert interval: yellow from (hours)",
  "audit.d1_neutral_ceiling_hours": "Advert interval: neutral ceiling (hours)",
  "audit.snr_yellow_from_db": "SNR yellow from (dB)",
  "audit.snr_green_from_db": "SNR green from (dB)",
  "audit.signal_best_avg_spread_db": "Signal: best/average spread flag from (dB)",
  "audit.d2_cluster_min_size": "Boot loop: cluster minimum size",
  "audit.d4_min_hash_width_bytes": "Path hash width: minimum width considered narrow (bytes)",
  "audit.reachability_proven_min_observations": "Reachability proven minimum observations",
  "audit.reachability_max_plausible_miles": "Reachability max plausible distance (miles)",
  "audit.advanced.measured_recall_p_connected_radio": "Measured recall, connected-radio (fraction, 0–1)",
  "audit.advanced.measured_recall_p_firehose": "Measured recall, firehose (fraction, 0–1)",
  "airtime.backfill_lookback_days": "Backfill look-back (days)",
  "airtime.backfill_batch_hours": "Backfill batch size (hours)",
  "airtime.retention_days": "Retention (days)",
  "mesh_health.retention_days": "Retention (days)",
  "mesh_health.min_observers_for_reach": "Minimum observers for reach",
  "mesh_health.rf_util_amber_pct": "RF Health: utilization turns amber at (%)",
  "mesh_health.rf_util_red_pct": "RF Health: utilization turns red at (%)",
  "mesh_health.rf_noise_amber_dbm": "RF Health: noise floor turns amber above (dBm)",
  "mesh_health.rf_noise_red_dbm": "RF Health: noise floor turns red above (dBm)",
  "mesh_health.rf_reach_green_pct": "Flood Reach: Green From (%)",
  "mesh_health.rf_reach_yellow_pct": "Flood Reach: Yellow From (%)",
  "mesh_health.miss_min_packets": "Missed By Feeder: Min Packets",
  "mesh_health.miss_min_peers": "Missed By Feeder: Min Peers",
  "mesh_health.miss_min_scope_evidence": "Missed By Feeder: Min Scope Evidence",
  "mesh_health.miss_tukey_multiplier": "Missed By Feeder: Tukey Multiplier",
  "mesh_health.miss_yellow_margin_pts": "Missed By Feeder: Yellow Margin (Points)",
  "mesh_health.miss_baseline_days": "Missed By Feeder: Baseline Days",
  "mesh_regions.outlines_enabled": "Load region outlines from MeshMapper",
  "mesh_regions.outlines_countries": "Countries",
  "meshmapper.enabled": "Show MeshMapper coverage",
  "meshmapper.refresh_minutes": "Refresh every (minutes)",
  "freqmapper.enabled": "Show FreqMapper coverage",
  "freqmapper.refresh_minutes": "Refresh every (minutes)",
  "route_inference.max_single_hop_miles": "Max single-hop distance (miles)",
  "ask.max_answer_tokens": "Max answer tokens",
  "ask.context_max_nodes": "Max nodes in context",
  "ask.context_max_findings": "Max findings in context",
  "auth.required": "Authentication required",
  "auth.session.lifetime_hours": "Session lifetime (hours)",
  "auth.session.cookie_name": "Session cookie name",
  "auth.session.cookie_secure": "Force secure session cookie",
  "auth.api_tokens.default_lifetime_days": "Default API token lifetime (days)",
  "auth.password_policy.min_length": "Minimum password length",
  "auth.oidc.display_label": "OIDC login button label",
  "auth.oidc.scopes": "OIDC scopes",
  "auth.oidc.role_mapping.groups_claim": "OIDC groups claim name",
  "auth.oidc.role_mapping.admin_groups": "OIDC admin groups",
  "auth.oidc.role_mapping.viewer_groups": "OIDC viewer groups",
  "auth.oidc.role_mapping.default_role": "OIDC default role",
  "observers.poll_defaults.max_retries": "Default max retries",
  "mqtt.defaults.topic_filters": "Default topic filters",
  "mqtt.defaults.root_topics": "Default root topics",
  "mqtt.defaults.collision_reconnect_threshold": "Default collision reconnect threshold",
  "messaging.chunking.meshtastic_max_payload_bytes": "Meshtastic max payload (bytes)",
  "hazards.poll_defaults.max_retries": "Default max retries",
  "api.pagination.ask_default_limit": "Ask default limit",
  "api.pagination.ask_max_limit": "Ask max limit",
  "api.pagination.audit_default_limit": "Analysis default limit",
  "api.pagination.audit_max_limit": "Analysis max limit",
  "api.pagination.history_default_limit": "History default limit",
  "api.pagination.history_max_limit": "History max limit",
  "api.pagination.meshview_packets_default_limit": "Meshview packets default limit",
  "api.pagination.meshview_packets_max_limit": "Meshview packets max limit",
  "api.pagination.meshview_receptions_default_limit": "Meshview receptions default limit",
  "api.pagination.meshview_receptions_max_limit": "Meshview receptions max limit",
  "api.pagination.meshview_route_tree_default_limit": "Meshview route-tree default limit",
  "api.pagination.meshview_route_tree_max_limit": "Meshview route-tree max limit",
  "api.pagination.messages_default_limit": "Messages default limit",
  "api.pagination.messages_max_limit": "Messages max limit",
  "api.pagination.packets_default_limit": "Packets default limit",
  "api.pagination.packets_max_limit": "Packets max limit",
  "meshcore.channels": "MeshCore channels",
  "position_estimate.estimate_radius_coverage_pct": "Estimate radius coverage (%)",
  "map.default_center_lat": "Default map center latitude",
  "map.default_center_lon": "Default map center longitude",
  "map.default_zoom": "Default map zoom level",
  "nodes.auto_retire_hours": "Auto-Retire After (hours)",
  "display.temperature_unit": "Temperature unit",
  "display.distance_unit": "Distance unit",

  // --- bare keys: dynamic_keyed_sections (transports) + list_sections item
  // fields, as documented in this block's own header comment above --------
  id: "ID",
  tls: "TLS",
  client_id: "Client ID",
  username_ref: "Username reference",
  password_ref: "Password reference",
  keepalive_s: "Keepalive interval (seconds)",
  reconnect_initial_s: "Initial reconnect backoff (seconds)",
  reconnect_max_s: "Maximum reconnect backoff (seconds)",
  collision_window_s: "Collision window (seconds)",
  keepalive_interval_s: "Keepalive interval (seconds)",
  station_lat: "Station latitude",
  station_lon: "Station longitude",
  base_url: "Base URL",
  timeout_s: "Timeout (seconds)",
  backoff_initial_s: "Initial backoff (seconds)",
  backoff_max_s: "Maximum backoff (seconds)",
  poll_interval_s: "Poll interval (seconds)",
  corescope_packets_enabled: "CoreScope packets enabled",
  corescope_observer_id: "CoreScope observer ID",
  corescope_observations_enabled: "CoreScope observations enabled",
  stats_poll_interval_s: "Stats poll interval (seconds)",
  link_keepalive_interval_s: "Link keepalive interval (seconds)",
  min_lat: "Min latitude",
  max_lat: "Max latitude",
  min_lon: "Min longitude",
  max_lon: "Max longitude",
  nws_zones: "NWS zones",
  region_id: "Region ID",
  secret_ref: "Secret reference",
  psk_ref: "PSK reference",
  // Remaining bare keys (Settings copy-lint pass, 2026-09-27): the same
  // "one shared label per bare key, checked for a meaning collision
  // first" rule as the rest of this block.
  aliases: "Aliases",
  channel: "Channel",
  cities: "Cities",
  collision_reconnect_threshold: "Collision reconnect threshold",
  conduit_source: "Conduit source",
  description: "Description",
  enabled: "Enabled",
  family: "Family",
  general_packets_enabled: "General packets enabled",
  host: "Host",
  ingest_packets: "Ingest packets",
  kind: "Transport kind",
  label: "Display label",
  local_name: "Local name",
  max_retries: "Max retries",
  min_severity: "Minimum severity",
  name: "Name",
  page_limit: "Page limit",
  port: "Port",
  root_topics: "Root topics",
  stats_source: "Statistics source",
  topic_filters: "Topic filters",
  transport: "Transport",
  trust_tier: "Trust tier",
  type: "Type",

  // --- Meshtastic device/module config field overrides (bare field name,
  // verified against config_pb2/module_config_pb2/channel_pb2 in the
  // installed meshtastic==2.7.7 package) — RadioConfigSection typed forms,
  // 2026-09-22. Same rule as above: only entries the mechanical fallback
  // gets wrong (an "Rx"/"Gps"/"Mqtt" acronym left partially lower-cased, a
  // brand's own capitalization) are listed here; e.g. spread_factor,
  // coding_rate and bandwidth are deliberately absent because "Spread
  // Factor" etc. is already correct mechanically.
  tx_power: "TX power (dBm)",
  tx_enabled: "TX enabled",
  rx_gpio: "GPS RX GPIO pin",
  tx_gpio: "GPS TX GPIO pin",
  gps_en_gpio: "GPS enable GPIO pin",
  gps_mode: "GPS mode",
  gps_enabled: "GPS enabled",
  gps_update_interval: "GPS update interval (seconds)",
  gps_attempt_time: "GPS attempt time",
  button_gpio: "Button GPIO pin",
  buzzer_gpio: "Buzzer GPIO pin",
  oled: "OLED type",
  use_pwm: "Use PWM",
  ble_threshold: "BLE RSSI threshold",
  wifi_enabled: "WiFi enabled",
  wifi_ssid: "WiFi SSID",
  wifi_psk: "WiFi PSK",
  eth_enabled: "Ethernet enabled",
  ipv4_config: "IPv4 config",
  ipv6_enabled: "IPv6 enabled",
  ntp_server: "NTP server",
  sds_secs: "Super deep sleep (seconds)",
  ls_secs: "Light sleep (seconds)",
  adc_multiplier_override: "ADC multiplier override",
  device_battery_ina_address: "Device battery INA i2c address",
  sx126x_rx_boosted_gain: "SX126x RX boosted gain",
  // Added 2026-09-22: present in meshtastic==2.7.11 (the pinned/generated
  // version) but not the 2.7.7 this block was first checked against.
  fem_lna_mode: "FEM/LNA mode",
  serial_hal_only: "Serial HAL only",
  // Remaining genuine acronym/initialism cases found by sweeping every
  // field name in meshtasticSchema.generated.ts against this map (audio,
  // canned-message and display module fields the earlier pass didn't
  // cover) — everything else that sweep flagged (e.g. rotary1_enabled,
  // powermon_enables) reads fine mechanically and is deliberately absent.
  i2s_din: "I2S DIN pin",
  i2s_sck: "I2S SCK pin",
  i2s_sd: "I2S SD pin",
  i2s_ws: "I2S WS pin",
  ring_tone_id: "Ring tone ID",
  screen_rgb_color: "Screen RGB color",
  inputbroker_event_cw: "Input broker event (CW)",
  inputbroker_event_ccw: "Input broker event (CCW)",
  pa_fan_disabled: "PA fan disabled",
  ignore_mqtt: "Ignore MQTT",
  config_ok_to_mqtt: "Config OK to MQTT",
  tls_enabled: "TLS enabled",
  json_enabled: "JSON enabled",
  tzdef: "Timezone (POSIX TZ string)",
  psk: "PSK (channel key)",

  // --- Radio snapshot presentation rebuild (Settings > Node Configuration
  // > Live snapshot, 2026-09-22) — replacing the raw PayloadView dump with
  // readable labels. Bare keys, so shared with every other consumer of
  // this map; checked each one below against the existing entries above
  // for a meaning collision before adding it (none found — see the
  // per-field notes).
  //
  // MeshCore identity/device fields: not in any protobuf schema (MeshCore
  // has no generated schema file the way Meshtastic does), so these are
  // sourced from the backend's own field construction, read directly —
  // backend/amalgamesh/transports/meshcore_tcp.py lines ~448-465 build
  // identity.adv_type from self_info()'s own "adv_type" key, and
  // device.{tx_power,max_tx_power,radio_freq,radio_bw,radio_sf,radio_cr}
  // from the matching self_info() keys of the same names. The same file's
  // _ctl_set_radio (~line 682) confirms the semantic meaning of the last
  // four: it writes them via the installed meshcore library's
  // set_radio(frequency, bandwidth, spreading_factor, coding_rate) — i.e.
  // radio_freq/radio_bw/radio_sf/radio_cr ARE literally frequency,
  // bandwidth, spreading factor, and coding rate, not a guess from the
  // field name alone. tx_power's meaning/unit (dBm) reuses the existing
  // Meshtastic tx_power entry above — same physical concept, same unit,
  // deliberately not a second, differently-worded label for the same
  // thing; max_tx_power is that field's own device-reported ceiling, same
  // unit. Units for radio_freq/radio_bw baked directly into the label
  // (MHz/kHz) rather than through configFieldRange, since these are
  // MeshCore's own flat fields, not a Meshtastic schema field with a
  // range-registry entry — 910.525 in a 900 MHz LoRa band and 62.5 as a
  // LoRa bandwidth value make the unit unambiguous, per direct inspection
  // of the live value alongside the field's own name.
  max_tx_power: "Maximum TX power (dBm)",
  radio_freq: "Frequency (MHz)",
  radio_bw: "Bandwidth (kHz)",
  radio_sf: "Spreading factor",
  radio_cr: "Coding rate",
  // adv_type's VALUE is a MeshCore enum (see MESHCORE_ADV_TYPE_LABEL below
  // for the mapping and its own two independent sources) — this is only
  // the field's own label.
  adv_type: "Advertised node type",

  // Meshtastic identity fields (RadioConfigEntry.identity, backend-built —
  // amalgamesh/transports/meshtastic_tcp.py's own identity dict, plain
  // snake_case keys, not protobuf jsonName camelCase) and the matching
  // device.metadata keys (real protobuf DeviceMetadata, MessageToDict
  // camelCase, converted to snake_case before this lookup — see
  // radioSnapshot.ts's camelToSnake). Only overriding where the mechanical
  // fallback is actually wrong; "long_name"/"short_name"/"firmware_version"/
  // "device_state_version"/"role" already render correctly mechanically and
  // are deliberately absent.
  node_num: "Node number",
  node_id: "Node ID",
  hw_model: "Hardware model",
  has_wifi: "Has WiFi",
  has_pkc: "Has public key cryptography (PKC)",
  // Same "Lora" -> "LoRa" acronym-casing fix already applied to the "lora"
  // section label itself (MESHTASTIC_SECTION_LABEL below) — the mechanical
  // fallback would otherwise render this live module_config.neighborInfo
  // field as "Transmit Over Lora".
  transmit_over_lora: "Transmit over LoRa",
};


// Mechanical fallback for any path with no registry entry above — moved
// here from SettingsPage.tsx verbatim (was a local `humanize` function
// there) so both the label registry and its fallback live in one place.
// Never returns blank: an unrecognised path still title-cases into
// something readable.
export function humanizeConfigPath(path: string): string {
  return path.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}


function lastConfigPathSegment(path: string): string {
  const idx = path.lastIndexOf(".");
  return idx === -1 ? path : path.slice(idx + 1);
}


export function configSectionLabel(sectionPath: string): string {
  return CONFIG_SECTION_LABEL[sectionPath] ?? humanizeConfigPath(sectionPath);
}


export function configFieldLabel(fieldPath: string): string {
  return CONFIG_FIELD_LABEL[fieldPath] ?? humanizeConfigPath(lastConfigPathSegment(fieldPath));
}


// --- Radio control: plain-operation param seeding (RadioControlOperationCard
// blank-form fix, 2026-09-23) -----------------------------------------------
// Maps a radio-control operation's PARAM NAME (as GET /api/radio/control/
// capabilities' `RadioControlParamSpec.name` declares it) to the dotted path,
// within one transport's GET /api/radio/config snapshot entry
// (RadioConfigEntry), where that same live value is already reported — so a
// plain operation form (ParamField in RadioControlOperationCard.tsx) can
// pre-fill from the device's real current state, the same way
// MeshtasticValuesForm already does for set_config/set_module_config/
// set_channel's typed fields. Keyed on param name, deliberately NEVER on an
// operation id: RADIO_CONTROL_CONTRACT.md section 2 forbids the frontend
// hardcoding an operation id anywhere, and a param name is the one thing
// both the capabilities registry and this seed map can agree on without
// either side knowing the other's operation list. A path that resolves to
// undefined/null on a given transport (e.g. `effective_position` is null on
// MeshCore today) means simply "no seed for this transport right now" — the
// field renders blank exactly as it did before this map existed; nothing
// here ever substitutes a zero or placeholder for a genuinely missing value.
export const RADIO_CONTROL_PARAM_SNAPSHOT_PATH: Record<string, string> = {
  frequency: "device.radio_freq",
  bandwidth: "device.radio_bw",
  spreading_factor: "device.radio_sf",
  coding_rate: "device.radio_cr",
  dbm: "device.tx_power",
  name: "identity.name",
  long_name: "identity.long_name",
  short_name: "identity.short_name",
  latitude: "effective_position.lat",
  longitude: "effective_position.lon",
};


// --- Meshtastic device config: enum labels + semantic numeric ranges
// (RadioConfigSection typed-forms rebuild, 2026-09-22) ----------------------
// frontend/src/lib/meshtasticSchema.generated.ts (owned by separate,
// concurrent work) is generated straight from the Meshtastic protobufs: it
// is the source of truth for which fields exist, their wire types, and enum
// membership. What protobufs cannot express is what a human should be shown,
// or what a number means beyond its wire type — a uint32 is just a uint32
// whether it holds a GPIO pin, a percentage, or a spread factor. This block
// supplies exactly those two things the schema can't: human labels for enum
// values (configEnumLabel, alongside the bare-key additions already made to
// CONFIG_FIELD_LABEL above) and semantic ranges (CONFIG_FIELD_RANGE). As
// with CONFIG_FIELD_LABEL/CONFIG_SECTION_LABEL above, every map here is a
// REGISTRY OF OVERRIDES on top of a mechanical fallback — not a hand-typed
// listing of every device/module config field or enum value. A field or
// enum value this registry has never heard of still renders, just via the
// mechanical rule or with no range constraint, never blank.
//
// Every key below was checked against the installed `meshtastic` protobuf
// package (config_pb2 / module_config_pb2 / channel_pb2, meshtastic==2.7.7)
// and, where noted, against the commented .proto source or MeshMonitor's own
// reference UI (components/configuration/*.tsx in ~/projects/meshmonitor) —
// never invented from memory. Two ranges suggested as "known-good" when this
// registry was speced were dropped because they didn't verify: tx_power
// (MeshMonitor's own txPower input carries no min/max) and fixed-position
// latitude/longitude (the real protobuf fields are latitude_i/longitude_i —
// fixed-point integers scaled by 1e7 — so a -90..90/-180..180 degree range,
// which is only true of MeshMonitor's converted decimal-degree display
// value, would be wrong applied to the raw field).

// Mechanical rule: lowercase, then title-case each underscore-separated
// word ("ROUTER_LATE" -> "Router Late"). Correct for most protobuf enum
// values as-is; CONFIG_ENUM_LABEL below overrides only the cases where
// that reads wrong.
export function configEnumLabel(rawName: string): string {
  if (rawName in CONFIG_ENUM_LABEL) return CONFIG_ENUM_LABEL[rawName];
  return rawName
    .toLowerCase()
    .split("_")
    .map((word) => (word.length === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join(" ");
}


export const CONFIG_ENUM_LABEL: Record<string, string> = {
  // --- Found by sweeping every enum value name in
  // meshtasticSchema.generated.ts against this map, 2026-09-22: a handful
  // of Meshtastic enums (NetworkConfig.AddressMode, PositionConfig's
  // GpsCoordinateFormat, TAK's MemberRole) use initialisms or PascalCase
  // values rather than SCREAMING_SNAKE_CASE — configEnumLabel's mechanical
  // rule (lowercase, then title-case each underscore-separated word) has no
  // delimiter to split a PascalCase name like "ForwardObserver" on, so it
  // renders as one run-together word without these overrides.
  DHCP: "DHCP",
  DEC: "Decimal degrees",
  DMS: "DMS (degrees, minutes, seconds)",
  UTM: "UTM (Universal Transverse Mercator)",
  MGRS: "MGRS (Military Grid Reference System)",
  OLC: "OLC (Open Location Code)",
  OSGR: "OSGR (Ordnance Survey Grid Reference)",
  MLS: "MLS (Maidenhead Locator System)",
  HQ: "HQ",
  RTO: "RTO (radio-telephone operator)",
  ForwardObserver: "Forward Observer",
  TeamLead: "Team Lead",
  TeamMember: "Team Member",
  // --- LoRaConfig.RegionCode: every value in the enum (config.proto).
  // Region codes are an operator's legal/regulatory choice, so the raw code
  // is kept alongside a gloss rather than replaced by one ------------------
  UNSET: "Unset (not configured)",
  US: "US (United States)",
  EU_433: "EU 433 (Europe 433 MHz)",
  EU_868: "EU 868 (Europe 868 MHz)",
  CN: "CN (China)",
  JP: "JP (Japan)",
  ANZ: "ANZ (Australia / New Zealand)",
  KR: "KR (Korea)",
  TW: "TW (Taiwan)",
  RU: "RU (Russia)",
  IN: "IN (India)",
  NZ_865: "NZ 865 (New Zealand 865 MHz)",
  TH: "TH (Thailand)",
  LORA_24: "LoRa 2.4 GHz",
  UA_433: "UA 433 (Ukraine 433 MHz)",
  UA_868: "UA 868 (Ukraine 868 MHz)",
  MY_433: "MY 433 (Malaysia 433 MHz)",
  MY_919: "MY 919 (Malaysia 919 MHz)",
  SG_923: "SG 923 (Singapore 923 MHz)",
  PH_433: "PH 433 (Philippines 433 MHz)",
  PH_868: "PH 868 (Philippines 868 MHz)",
  PH_915: "PH 915 (Philippines 915 MHz)",
  ANZ_433: "ANZ 433 (Australia / New Zealand 433 MHz)",
  KZ_433: "KZ 433 (Kazakhstan 433 MHz)",
  KZ_863: "KZ 863 (Kazakhstan 863 MHz)",
  NP_865: "NP 865 (Nepal 865 MHz)",
  BR_902: "BR 902 (Brazil 902 MHz)",
  // Added 2026-09-22: these 7 exist in meshtastic==2.7.11 (the version
  // actually pinned in backend/pyproject.toml and generated against in
  // meshtasticSchema.generated.ts) but not in the 2.7.7 install this
  // registry was first checked against — sourced from config.proto's own
  // comments (~/projects/meshmonitor/protobufs/meshtastic/config.proto)
  // rather than left to guesswork, to actually cover "every value" as
  // regulatory correctness requires.
  ITU1_2M: "ITU1 2m (Region 1 amateur 144-146 MHz)",
  ITU2_2M: "ITU2 2m (Region 2 amateur 144-148 MHz)",
  ITU3_2M: "ITU3 2m (Region 3 amateur 144-148 MHz)",
  EU_866: "EU 866 (Europe 866 MHz SRD)",
  EU_874: "EU 874 (Europe 874 MHz SRD)",
  EU_917: "EU 917 (Europe 917 MHz SRD)",
  EU_N_868: "EU-N 868 (Europe 868 MHz, narrow presets)",

  // --- DisplayConfig.OledType: driver part numbers, kept as-is rather than
  // mechanically mangled into "Oled Ssd1306" --------------------------------
  OLED_AUTO: "Auto-detect",
  OLED_SSD1306: "SSD1306",
  OLED_SH1106: "SH1106",
  OLED_SH1107: "SH1107",
  OLED_SH1107_128_128: "SH1107 (128x128)",
  OLED_SH1107_ROTATED: "SH1107 (rotated, 64x128)",

  // --- SerialConfig.Serial_Mode: acronyms/brand names the mechanical rule
  // gets wrong, verified against module_config.proto's own comments --------
  TEXTMSG: "Text message",
  NMEA: "NMEA",
  CALTOPO: "CalTopo",
  WS85: "WS85 (Ecowitt weather station)",
  VE_DIRECT: "VE.Direct",
  MS_CONFIG: "MeshSolar config",
  LOGTEXT: "Log (text only)",

  // --- DeviceConfig.Role: ATAK integration is a fixed product name, not a
  // mechanically-derivable word ---------------------------------------------
  TAK: "TAK (ATAK)",
  TAK_TRACKER: "TAK Tracker (ATAK)",

  // --- LoRaConfig.ModemPreset: the base name is already correct
  // mechanically ("Long Fast"); the radio parameters below are sourced from
  // MeshMonitor's own MODEM_PRESET_OPTIONS table
  // (components/configuration/constants.ts) and added as a gloss because
  // they're genuinely useful, not because the name needed fixing.
  // VERY_LONG_SLOW is omitted: no sourced parameters for it.
  LONG_FAST: "Long Fast (BW 250 kHz, SF 11, CR 4/5)",
  LONG_SLOW: "Long Slow (BW 125 kHz, SF 12, CR 4/8)",
  MEDIUM_SLOW: "Medium Slow (BW 250 kHz, SF 10, CR 4/5)",
  MEDIUM_FAST: "Medium Fast (BW 250 kHz, SF 9, CR 4/5)",
  SHORT_SLOW: "Short Slow (BW 250 kHz, SF 8, CR 4/5)",
  SHORT_FAST: "Short Fast (BW 250 kHz, SF 7, CR 4/5)",
  LONG_MODERATE: "Long Moderate (BW 125 kHz, SF 11, CR 4/8)",
  SHORT_TURBO: "Short Turbo (BW 500 kHz, SF 7, CR 4/5)",
  LONG_TURBO: "Long Turbo (BW 500 kHz, SF 11, CR 4/5)",
  // Added 2026-09-22, present in meshtastic==2.7.11 but not the 2.7.7 this
  // block was first checked against — description text (not radio
  // parameters, which config.proto's comments don't give for these four)
  // sourced from config.proto's own comments.
  LITE_FAST: "Lite Fast (EU 47b-compliant, comparable to Medium Fast)",
  LITE_SLOW: "Lite Slow (EU 47b-compliant, comparable to Long Fast)",
  NARROW_FAST: "Narrow Fast (reduced interference footprint)",
  NARROW_SLOW: "Narrow Slow (comparable range/rate to Long Fast)",
};


export interface ConfigFieldRange {
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly unit?: string;
}


// Source: MeshMonitor's own reference UI — the <input min/max/step>
// constraints in components/configuration/{LoRaConfigSection,
// DeviceConfigSection,PositionConfigSection}.tsx in ~/projects/meshmonitor —
// not invented. A field the protobuf's wire type allows a much wider range
// for (e.g. a uint32's 0..4294967295) but that has no known enforced
// minimum/maximum beyond what's listed here just renders with no
// constraint, same as any unregistered field.
export const CONFIG_FIELD_RANGE: Record<string, ConfigFieldRange> = {
  hop_limit: { min: 1, max: 7 },
  spread_factor: { min: 7, max: 12 },
  coding_rate: { min: 5, max: 8 },
  bandwidth: { min: 1, max: 500, unit: "kHz" },
  frequency_offset: { step: 0.001 },
  node_info_broadcast_secs: { min: 3600, unit: "s" },
  position_broadcast_secs: { min: 32, unit: "s" },
  channel_num: { min: 0, max: 255 },
};


export function configFieldRange(fieldName: string): ConfigFieldRange | undefined {
  return CONFIG_FIELD_RANGE[fieldName];
}


// --- MeshCore identity.adv_type enum (radio snapshot rebuild, 2026-09-22) --
// MeshCore's own installed Python package (the one this backend runs —
// /usr/local/lib/python3.12/site-packages/meshcore inside amalgamesh-backend,
// meshcore_parser.py line ~184 and reader.py line ~168) parses adv_type as
// a raw byte with NO name table at all — checked directly, not assumed
// absent. No enumeration exists there to cite.
//
// An authoritative mapping WAS found in two independent, real MeshCore
// client implementations installed on this machine (both citing the
// upstream MeshCore protocol docs, github.com/meshcore-dev/MeshCore), which
// agree exactly on all five values:
//   ~/projects/meshmonitor/node_modules/@liamcottle/meshcore.js/src/advert.js
//     (lines 6-10): ADV_TYPE_NONE=0, ADV_TYPE_CHAT=1, ADV_TYPE_REPEATER=2,
//     ADV_TYPE_ROOM=3, ADV_TYPE_SENSOR=4.
//   ~/projects/meshmonitor/node_modules/@michaelhart/meshcore-decoder/src/types/enums.ts
//     (lines 39-45): DeviceRole.Unknown=0, ChatNode=1, Repeater=2,
//     RoomServer=3, Sensor=4.
// Two independently-authored libraries agreeing exactly is treated as
// authoritative here — per this task's own rule, a wrong invented name
// would be worse than the raw integer, so this is only used because it
// verified, not guessed. A live entry (transport_id "meshcore_vnode",
// a companion-node name) reporting adv_type 1 — "Chat/companion" — matches its own
// role as a companion/vnode bridge, a real cross-check, not just a
// name-table lookup taken on faith.
export const MESHCORE_ADV_TYPE_LABEL: Record<number, string> = {
  0: "None",
  1: "Chat / companion node",
  2: "Repeater",
  3: "Room server",
  4: "Sensor",
};


// Renders BOTH the raw integer and the gloss (e.g. "1 (Chat / companion
// node)") — never the gloss alone — so an unrecognised future value (or a
// mapping this registry got wrong) is always still visible as the real
// reported number, per this file's "never invent, never hide the raw fact"
// convention used throughout (see e.g. TRUST_TIER_INFO's own unknown-tier
// handling above).
export function meshcoreAdvTypeLabel(rawValue: number): string {
  const known = MESHCORE_ADV_TYPE_LABEL[rawValue];
  return known ? `${rawValue} (${known})` : String(rawValue);
}


// Meshtastic Config/ModuleConfig SECTION names (the "device"/"lora"/"mqtt"/
// etc. keys of meshtasticSchema.generated.ts's MESHTASTIC_CONFIG_SECTIONS/
// MESHTASTIC_MODULE_SECTIONS), for the section headings in the typed radio
// config forms (RadioControlOperationCard's Meshtastic values form).
// Deliberately a SEPARATE map from CONFIG_SECTION_LABEL/configSectionLabel
// above rather than added entries there: that registry is shared with
// amalgamesh's own config.yaml section paths (an entirely different
// system), and a bare key like "mqtt" or "telemetry" here must never
// silently collide with an unrelated config.yaml section of the same name.
// Same override-on-a-mechanical-fallback rule as everywhere else in this
// file: only listed where the mechanical title-casing is wrong (an
// initialism, or "Lora"/"Ui" that should read "LoRa"/"UI").
export const MESHTASTIC_SECTION_LABEL: Record<string, string> = {
  lora: "LoRa",
  device_ui: "Device UI",
  mqtt: "MQTT",
  sessionkey: "Session key",
  external_notification: "External notification",
  store_forward: "Store & forward",
  range_test: "Range test",
  canned_message: "Canned message",
  remote_hardware: "Remote hardware",
  neighbor_info: "Neighbor info",
  ambient_lighting: "Ambient lighting",
  detection_sensor: "Detection sensor",
  paxcounter: "PAX counter",
  statusmessage: "Status message",
  traffic_management: "Traffic management",
  tak: "TAK (ATAK)",
};


export function meshtasticSectionLabel(sectionName: string): string {
  return MESHTASTIC_SECTION_LABEL[sectionName] ?? humanizeConfigPath(sectionName);
}


// Render order for the stacked Meshtastic Config/ModuleConfig section cards
// in Settings > Node Configuration > Radio control (RadioControlPanel.tsx).
// Mirrors the render order of MeshMonitor's src/components/ConfigurationTab.tsx
// (the reference implementation for this surface: every
// section rendered stacked and visible, no chooser). A key not listed here is
// appended after these, in the order the API's `choices` list reports it.
// Intentionally spans both Config and ModuleConfig section keys, because
// MeshMonitor itself interleaves Config and ModuleConfig sections into one
// stack rather than grouping them separately.
export const MESHTASTIC_SECTION_ORDER: readonly string[] = [
  "device",
  "lora",
  "position",
  "power",
  "display",
  "telemetry",
  "mqtt",
  "neighbor_info",
  "network",
  "external_notification",
  "store_forward",
  "range_test",
  "canned_message",
  "audio",
  "remote_hardware",
  "detection_sensor",
  "paxcounter",
  "traffic_management",
  "serial",
  "ambient_lighting",
  "security",
  "bluetooth",
];


// --- Settings app categories (Settings page rebuild, 2026-09-21) -----------
// The Settings page moved from a flat stack of 25 accordion sections to a
// real app shell: a persistent left category menu, with sections grouped
// into a handful of categories and a tab strip when a category holds more
// than one section. CONFIG_CATEGORIES is the fixed, ordered menu; the menu
// itself only ever shows a category that actually has at least one section
// in it (an empty category would just be a dead button).
//
// `node` is NOT schema-driven — it's the one category that isn't walking
// GET /api/config/schema at all. It holds RadioConfigSection, the live
// device-state panel (identity/position/device config as reported by the
// radios themselves), which predates and sits outside the config schema
// entirely. Every other category is populated by walking the schema.
export interface ConfigCategory {
  id: string;
  label: string;
  description?: string;
}


export const CONFIG_CATEGORIES: ConfigCategory[] = [
  { id: "node", label: "Node Configuration" },
  { id: "audit", label: "Audit" },
  { id: "connections", label: "Connections" },
  { id: "observers", label: "Observers & Sources" },
  { id: "regions", label: "Regions" },
  { id: "alerting", label: "Alerting" },
  { id: "data", label: "Data & Retention" },
  { id: "access", label: "Access" },
  { id: "system", label: "System" },
  { id: "advanced", label: "Advanced" },
];


// Path-PREFIX map from a config section's own path to the category it
// belongs in. A dotted list-section path (e.g. "mqtt.brokers") is not
// listed directly — it resolves through its own first segment ("mqtt")
// via configSectionCategory's longest-prefix walk below, so a new list
// section nested under an already-categorized flat section is placed
// correctly without needing its own entry here.
export const CONFIG_SECTION_CATEGORY: Record<string, string> = {
  // Settings-coverage research (part 4, gaps #9/#10): measured/engineering
  // constants nested under `audit` specifically so their config reads
  // reuse that section's existing per-request plumbing — but they are
  // "advanced" tuning, not operator-facing Audit policy. This
  // one nested-prefix entry categorizes them separately without touching
  // any of audit's other (non-"advanced") fields — the longest-prefix
  // walk below tries the full path first, so "audit.advanced.<field>"
  // matches THIS entry before ever falling back to "audit"'s own category.
  "audit.advanced": "advanced",
  transports: "connections",
  mqtt: "connections",
  observers: "observers",
  meshmapper: "observers",
  freqmapper: "observers",
  regions: "regions",
  mesh_regions: "regions",
  hazards: "alerting",
  retention: "data",
  reconciliation: "data",
  database: "data",
  audit: "audit",
  auth: "access",
  api: "access",
  server: "system",
  logging: "system",
  watchdog: "system",
  ask: "system",
  route_inference: "system",
  messaging: "system",
  meshcore: "system",
  // Settings-coverage pass (part 4, 2026-09-27): features.* governs
  // startup route/websocket registration (system-level, like server/
  // watchdog above); map.*/display.* are app-wide display prefs with no
  // existing category to sit in more naturally. position_estimate/
  // airtime/mesh_health are exactly the measured-constant groups
  // ADVANCED_FIELDS bundles in config_schema.py — same "advanced" bucket
  // audit.advanced already gets, made explicit rather than left to the
  // longest-prefix walk's own "advanced" fallback below.
  features: "system",
  map: "system",
  display: "system",
  nodes: "system",
  position_estimate: "advanced",
  airtime: "advanced",
  mesh_health: "advanced",
};


// Audit settings are grouped by the operator task they support. The schema
// remains the source of field values and descriptions; these rules only
// organize the existing fields in the Settings UI.
export const AUDIT_SETTINGS_GROUPS = [
  { id: "adverts", label: "Adverts & traffic", description: "This group covers observed advert timing, traffic volume, and advert bursts or clock-reset evidence used by the boot-loop check. Check descriptions distinguish measured evidence and operator policy from cited firmware reference values." },
  { id: "clock", label: "Clock & signal", description: "Clock offset compares a device’s advert time with trusted receipt time. Signal thresholds color measured MeshCore link SNR; these are operator choices, not firmware limits." },
  { id: "reach", label: "Reach", description: "Reach figures are derived from receptions, receiver positions, and hop counts. The controls below govern repeat evidence, implausible distances, and the (0,0) position sentinel." },
  { id: "location", label: "Location", description: "Limits for Impossible Location. Max Link Distance is in Advanced." },
  { id: "topology", label: "Topology & scopes", description: "Topology metrics are derived from observed links, relayed traffic, scope tags, and positioned neighbours. Scope names add decoding candidates; umbrella scopes classify broadly shared tags. This group also contains the MeshCore path-hash-width policy threshold. Unset color thresholds leave metrics uncolored." },
  { id: "freshness", label: "Analysis freshness", description: "Analysis pages read completed report snapshots. A background pass refreshes those reports on its schedule; page loads do not wait for a new calculation. If a pass is delayed or stopped, the last completed report remains available." },
] as const;


export type AuditSettingsGroupId = (typeof AUDIT_SETTINGS_GROUPS)[number]["id"];


export function auditSettingsGroupForPath(path: string): AuditSettingsGroupId {
  const key = path.startsWith("audit.") ? path.slice("audit.".length) : path;
  if (key.startsWith("d1_") || key.startsWith("d2_") || key === "pkts_per_hr_chatty_from" || key === "top_list_count") return "adverts";
  if (key.startsWith("clock_") || key.startsWith("snr_") || key.startsWith("signal_")) return "clock";
  if (key.startsWith("location_")) return "location";
  if (key.startsWith("reachability_") || key === "null_island_epsilon_deg") return "reach";
  if (key === "d4_min_hash_width_bytes") return "topology";
  if (key.startsWith("backbone_") || key === "scope_names" || key === "footprint_min_off_share_pct" || key.startsWith("scope_")) return "topology";
  if (key.startsWith("history_") || key === "parallel_groups" || key === "paths_arrival" || key === "rf_health_arrival" || key === "backbone_arrival" || key === "audit_table_arrival") return "freshness";
  if (["cache_ttl_s", "recompute_interval_minutes", "recalculate_cooldown_s", "snapshot_memory_limit_mb", "snapshot_memory_cache_mb"].includes(key)) return "freshness";
  return "topology";
}


// Longest-prefix resolution on dotted path segments: try the full path,
// then drop the last segment and retry, until a match is found. Falls back
// to "advanced" when nothing matches — the guarantee that keeps a future
// config section (one this map hasn't been taught about yet) from becoming
// invisible: it still renders, just under a generic catch-all category,
// rather than being silently dropped from the menu entirely.
export function configSectionCategory(sectionPath: string): string {
  let path = sectionPath;
  while (path.length > 0) {
    if (path in CONFIG_SECTION_CATEGORY) return CONFIG_SECTION_CATEGORY[path];
    const idx = path.lastIndexOf(".");
    if (idx === -1) break;
    path = path.slice(0, idx);
  }
  return "advanced";
}


// --- Settings <select> choice labels (fresh-install sweep) -----------------
// A config schema `choices` list is a fixed set of raw stored values (never
// hand-written labels — see config_schema.py's own FieldSpec.choices
// comment), so a settings <select> option must not just print the raw
// value verbatim (an operator seeing "first_party" in a dropdown has no
// way to know that means "our own radio"). Keyed by the raw choice value,
// shared across every field that happens to use that same vocabulary
// (e.g. every trust_tier field reuses the TRUST_TIER_INFO-derived labels
// below). A choice value with no entry here still renders (falls back to
// the raw value), so a future schema addition never goes silently missing
// from the UI — it just isn't humanized yet.
export const CONFIG_CHOICE_LABEL: Record<string, string> = {
  // Trust tiers (transports.trust_tier, observers.sources[].trust_tier,
  // mqtt.brokers[].trust_tier) — short names distinct from TRUST_TIER_INFO's
  // own longer `label`/`short`, sized for a dropdown option rather than a
  // badge.
  first_party: "First-party (our radio)",
  first_party_local_network: "First-party, local network relay",
  first_party_unsigned: "First-party, unsigned protocol",
  external_unauthenticated: "External, unauthenticated",
  relayed_mqtt: "Relayed via MQTT",
  simulated: "Simulated",
  unverified: "Unverified",
  unknown: "Unknown",
  // Transport kind (transports[].kind).
  meshtastic_tcp: "Meshtastic (TCP)",
  meshcore_tcp: "MeshCore (TCP)",
  // MeshCore companion stats source (transports[].stats_source).
  companion: "Companion",
  none: "None",
  // Display preference units (display.temperature_unit/distance_unit) —
  // "auto" is shared by both fields with the same meaning ("use the
  // browser's own locale").
  auto: "Auto (use browser locale)",
  c: "Celsius",
  f: "Fahrenheit",
  km: "Kilometers",
  mi: "Miles",
};


export function configChoiceLabel(raw: string): string {
  return CONFIG_CHOICE_LABEL[raw] ?? weekdayLabel(raw) ?? raw;
}


// --- Settings > Connections: plain labels and helper text -----------------------------------
// The backend's transport field descriptions (config_schema.py TRANSPORT_ITEM_FIELDS) carry the
// same helper text, so the API and the panel agree.
export const CONNECTION_FIELD_TEXT: Record<string, { label: string; help: string }> = {
  label: { label: "Name", help: "What this connection is called everywhere in Amalgamesh. You can change it at any time." },
  kind: { label: "Radio Type", help: "The firmware on the radio or virtual node at this address." },
  host: { label: "Host", help: "IP address or hostname of the radio or virtual node." },
  port: { label: "Port", help: "TCP port it listens on." },
  trust_tier: { label: "What We Trust It For", help: "" },
  station_position: {
    label: "Station Position",
    help: "Where this radio physically sits. Overrides the position the radio broadcasts. Clear it to use the broadcast again.",
  },
  station_lat: { label: "Latitude", help: "" },
  station_lon: { label: "Longitude", help: "" },
  ingest_packets: {
    label: "Store What It Hears",
    help: "Save packets from this connection. Turn it off for a second connection to a radio another connection already saves, so packets are not stored twice.",
  },
  reconnect_initial_s: { label: "First Retry After (s)", help: "Wait before the first reconnect attempt. Each failed retry waits twice as long as the last (backoff)." },
  reconnect_max_s: { label: "Longest Retry Gap (s)", help: "Backoff stops growing at this gap, so a radio that stays down is retried this often." },
  keepalive_interval_s: {
    label: "Quiet Timeout (s)",
    help: "How long a silent connection waits before it is treated as lost and reconnected.",
  },
  stats_source: {
    label: "Read Radio Stats",
    help: "Asks the radio for its own noise floor, airtime and counters over this connection (OpenHop or MeshCore companion). Never transmits.",
  },
  stats_poll_interval_s: { label: "Stats Every (s)", help: "How often to ask. MeshCore radios refresh about every 30 s." },
  link_keepalive_interval_s: {
    label: "Keep Quiet Link Alive (s)",
    help: "How often to send a harmless local ping to the radio or virtual node so it does not close a quiet connection. 0 turns it off. Skipped when stats are read at least this often. Never transmits.",
  },
  corescope_observer_id: {
    label: "CoreScope Observer ID",
    help: "If this radio also feeds CoreScope, its observer ID there. Adds a comparison of reported against measured airtime on Health. 64 hex characters, the radio's public key. Leave blank otherwise.",
  },
};


export const CONNECTION_KIND_LABEL: Record<string, string> = { meshtastic_tcp: "Meshtastic", meshcore_tcp: "MeshCore" };

export const CONNECTION_KIND_ORDER: readonly string[] = ["meshtastic_tcp", "meshcore_tcp"];
